import { useEffect, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/admin/auth/useAuthStore';
import type { RequiredPerms } from '@/admin/auth/permissions';
import { usePermStore } from '@/admin/auth/usePermStore';
import { getFirstAccessibleRoute } from '@/admin/utils/route.utils';

interface PermissionGuardProps {
  anyOf: string | RequiredPerms;
  children?: ReactNode;
}

/**
 * Route permission gate (M3 S3.1.2).
 *
 * - Stale perm cache → trigger `fetchPermissions()` once via useEffect
 *   (stale-while-revalidate, 60s TTL; never suspends, never throws).
 * - `has(anyOf)` → children (or <Outlet/> for route-element usage).
 * - Else if admin disabled → <Navigate to="/admin/disabled" replace />.
 * - Else → <Navigate to="/admin/no-access" replace state={{ from: pathname }} />
 *   (generic — never enumerates which permission is missing).
 */
export function PermissionGuard({ anyOf, children }: PermissionGuardProps) {
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const has = usePermStore((s) => s.has);
  const isStale = usePermStore((s) => s.isStale);
  const isFetching = usePermStore((s) => s.isFetching);

  useEffect(() => {
    try {
      if (isStale() && !isFetching) {
        void usePermStore.getState().fetchPermissions().catch(() => undefined);
      }
    } catch {
      // Never throw to the UI.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let allowed = false;
  try {
    allowed = has(anyOf);
  } catch {
    allowed = false;
  }
  if (allowed) return children ? <>{children}</> : <Outlet />;

  if (status === 'disabled') {
    return <Navigate to="/admin/disabled" replace />;
  }

  // Redirect to first accessible route instead of generic no-access page
  const redirectTarget = getFirstAccessibleRoute();
  // Avoid infinite redirect loops — if we're already at the target, go to no-access
  const finalTarget = redirectTarget === location.pathname ? '/admin/no-access' : redirectTarget;

  if (import.meta.env.DEV) {
     
    console.debug('[PermissionGuard] denied → redirecting to', finalTarget);
  }
  return (
    <Navigate
      to={finalTarget}
      replace
      state={{ from: location.pathname }}
    />
  );
}

export default PermissionGuard;
